'use client';

import { useSearchParams } from 'next/navigation';
import React, { useCallback, useMemo, useState } from 'react';

import ProgramFilter from '@/app/[locale]/[domain]/(default-layout)/program/_components/filter/ProgramFilter';
import ProgramHighlightsSection from '@/app/[locale]/[domain]/(default-layout)/program/_components/highlights/ProgramHighlightsSection';
import ProgramCompactGroupedList from '@/app/[locale]/[domain]/(default-layout)/program/_components/ProgramCompactGroupedList';
import ProgramCompactList from '@/app/[locale]/[domain]/(default-layout)/program/_components/ProgramCompactList';
import ProgramListCalendar from '@/app/[locale]/[domain]/(default-layout)/program/_components/ProgramListCalendar';
import ProgramListPlaceholder from '@/app/[locale]/[domain]/(default-layout)/program/_components/ProgramListPlaceholder';
import ShowingListItem from '@/app/[locale]/[domain]/(default-layout)/program/_components/ShowingListItem';
import ProgramShowingListSkeleton from '@/app/[locale]/[domain]/(default-layout)/program/_components/skeletons/ProgramShowingListSkeleton';

import { ContentDto } from '@cineamo/api/models/content/ContentDto.types';
import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';
import { WebsiteProgramDateFilter } from '@cineamo/api/models/website-configuration/WebsiteProgramDateFilter.enum';
import { WebsiteProgramListLayout } from '@cineamo/api/models/website-configuration/WebsiteProgramListLayout.enum';

import { hasMatchingGenre, hasMovieAccessibilityFeatures, isMovieReleaseDateNew } from '@/helpers/movie-helper';
import {
    programDayEnd,
    programDayKey,
    programDayKeys,
    programDayStart,
    todayProgramDayKey
} from '@/helpers/program-day';
import { calendarDaysBetween, localDateOfDayKey } from '@cineamo/ui/src/helpers/cinema-time-zone';
import { cn } from '@cineamo/ui/src/helpers/cn';

import { CachedContentData } from '@/hooks/useCachedContentData';
import useShowingsListFiltered from '@/hooks/useShowingsListFiltered';
import { useVirtualPagination } from '@/hooks/useVirtualPagination';

import KinofestBanner from '@cineamo/ui/src/misc/kinofest/KinofestBanner';
import { KinofestBannerItemSize } from '@cineamo/ui/src/misc/kinofest/KinofestBannerItem';

import MobileOrDesktopRendererSSR from '@cineamo/composables/src/MobileOrDesktopRendererSSR';
import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { useCinemaRooms } from '@/providers/CinemaRoomProvider';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { useProgramFilterContext } from '@/providers/ProgramFilterProvider';
import { daysBetween } from '@/utils/date-helper';
import { parseProgramDate } from '@/utils/parse-program-date';

import DatePicker from './DatePicker';

type ProgramListProps = {
    visibleDaysInFuture: number;
    allContents: CachedContentData[];
    allShowings: ShowingDto[];
    renderedAt: number;
};

type SelectedDate = string | 'all';

// Performance-optimized configuration for virtual pagination
const VIRTUAL_PAGINATION_CONFIG = {
    initialLoad: 10, // Initial showings to display
    loadMore: 10, // Showings to add when scrolling
    loadDebounce: 100, // ms between loads
    checkInterval: 200, // ms for continuous check
    rootMargin: '200px' // IntersectionObserver margin
} as const;

function ProgramList(props: ProgramListProps): React.JSX.Element {
    const { visibleDaysInFuture, renderedAt } = props;
    const domainConfiguration = useDomainConfiguration();
    const timeZone = domainConfiguration.timeZone;
    // Resolved once per mounted list instead of once per module evaluation: as a module constant a
    // warm server instance kept rendering the day it booted on while the browser resolved the real
    // one, so `selectedDate` — and with it the whole rendered list — differed on hydration.
    // Read on the cinema's clock, which is also what keeps server and browser agreeing when the two
    // sit in different zones.
    const [dateTodayFormatted] = useState(() => todayProgramDayKey(timeZone));
    const { track } = useAnalytics();
    const searchParams = useSearchParams();
    const { sorting, activeFilters } = useProgramFilterContext();
    const { getCinemaRoomById } = useCinemaRooms();
    const isNewFilterActive = activeFilters.isNew === 'true';
    const isGretaFilterActive = activeFilters.isGreta === 'true';
    const isWheelchairFilterActive = activeFilters.isWheelchairAccessible === 'true';
    const activeGenreIds = useMemo<string[]>(() => {
        const genreIds = activeFilters.genreIds;
        if (!genreIds) return [];
        return Array.isArray(genreIds) ? genreIds : [genreIds];
    }, [activeFilters.genreIds]);

    // Without a `date` query parameter the configured initial date filter
    // decides which tab is preselected ('today' unless configured otherwise).
    const configuredInitialDate =
        domainConfiguration?.websiteConfiguration?.initialProgramDateFilter === WebsiteProgramDateFilter.ALL
            ? 'all'
            : null;
    // Crawlers and old links send `?date=2026-13-01` and the like; an unparseable day threw in SSR
    // (RangeError: Invalid time value), so anything but 'all' or a real yyyy-MM-dd day is ignored.
    const dateParam = searchParams.get('date');
    const initialDate =
        dateParam === 'all' || parseProgramDate(dateParam ?? undefined) ? dateParam : configuredInitialDate;
    // Compared as yyyy-MM-dd strings (like the render-time sync below) rather than against a
    // timestamp, so the result cannot depend on the runtime's timezone.
    const validInitialDate =
        initialDate === 'all'
            ? 'all'
            : !initialDate || initialDate < dateTodayFormatted
              ? dateTodayFormatted
              : initialDate;
    const [selectedDate, setSelectedDate] = useState<SelectedDate>(validInitialDate);

    // this one is used to render amounts of showings for every date
    const { showings: allShowings, isFetchingShowingList: isFetchingAllShowingList } = useShowingsListFiltered({
        initialShowings: props.allShowings,
        initialShowingsUpdatedAt: renderedAt,
        selectedDate: 'all'
    });

    // this one is used to render contents and ticket buttons for selected date
    const { showings: dailyShowings, isFetchingShowingList: isFetchingDailyShowingList } = useShowingsListFiltered({
        initialShowings: props.allShowings,
        initialShowingsUpdatedAt: renderedAt,
        selectedDate: selectedDate
    });

    const isFetchingShowingList = isFetchingAllShowingList || isFetchingDailyShowingList;

    // Every row shows its own content's slice of `dailyShowings`. Grouping once here replaces a
    // filter pass over all of the cinema's showings in each row, which ran once per rendered layout.
    const showingsByContentId = useMemo(() => {
        const map = new Map<ContentDto['id'], ShowingDto[]>();
        dailyShowings.forEach((showing) => {
            const showings = map.get(showing.contentId);
            if (showings) {
                showings.push(showing);
            } else {
                map.set(showing.contentId, [showing]);
            }
        });
        return map;
    }, [dailyShowings]);

    const showingsCountForContentId = useMemo(
        () => new Map(Array.from(showingsByContentId, ([contentId, showings]) => [contentId, showings.length])),
        [showingsByContentId]
    );

    // Wheelchair filtering is showing-level (the showing's cinema room has the flag), but the default
    // desktop list is grouped by content — so a content stays visible when at least one of today's
    // showings runs in a wheelchair-accessible room.
    const contentIdsWithWheelchairAccessibleShowing = useMemo(() => {
        const set = new Set<ContentDto['id']>();
        dailyShowings.forEach((showing) => {
            if (getCinemaRoomById(showing.cinemaRoomId)?.hasWheelchairSpaces) {
                set.add(showing.contentId);
            }
        });
        return set;
    }, [dailyShowings, getCinemaRoomById]);

    const showingStartDatetimeForContentId = useMemo(() => {
        const map = new Map<ContentDto['id'], number>();
        dailyShowings.forEach((showing) => {
            if (
                !map.has(showing.contentId) ||
                new Date(showing.startDatetime).getTime() < (map.get(showing.contentId) ?? Infinity)
            ) {
                map.set(showing.contentId, new Date(showing.startDatetime).getTime());
            }
        });
        return map;
    }, [dailyShowings]);

    const contentSortingFunction = useCallback(
        (a: CachedContentData, b: CachedContentData) => {
            const startDatetimeComparison =
                (showingStartDatetimeForContentId.get(a.id) ?? 0) - (showingStartDatetimeForContentId.get(b.id) ?? 0);

            const order = ['date-desc', 'showings-desc', 'newest-desc', 'z-a'].includes(sorting) ? -1 : 1;
            let result = 0;

            switch (sorting) {
                case 'date-asc':
                case 'date-desc': {
                    // Sort showing time
                    result = order * startDatetimeComparison;
                    break;
                }

                case 'a-z':
                case 'z-a': {
                    // Sort alphabetically by title
                    result = order * (a.title ?? '').localeCompare(b.title ?? '');
                    break;
                }
                case 'showings-asc':
                case 'showings-desc': {
                    // Sort by number of showings
                    result =
                        order *
                        ((showingsCountForContentId.get(a.id) ?? 0) - (showingsCountForContentId.get(b.id) ?? 0));
                    break;
                }

                case 'newest-desc':
                case 'newest-asc': {
                    // Sort by release date (oldest first)
                    const aReleaseDate = a.releaseDate ? new Date(a.releaseDate).getTime() : 0;
                    const bReleaseDate = b.releaseDate ? new Date(b.releaseDate).getTime() : 0;
                    const releaseDateComparison = aReleaseDate - bReleaseDate;
                    result = order * releaseDateComparison;
                    break;
                }

                default:
                    // Default to date ascending
                    result = startDatetimeComparison;
            }
            return result === 0 ? startDatetimeComparison : result;
        },
        [showingsCountForContentId, showingStartDatetimeForContentId, sorting]
    );

    const filteredContents = useMemo(() => {
        return props.allContents
            .filter((content) => showingsCountForContentId.has(content.id))
            .filter((content) => !isNewFilterActive || isMovieReleaseDateNew(content.releaseDate, timeZone))
            .filter((content) => !isGretaFilterActive || hasMovieAccessibilityFeatures(content.movieAccessibility))
            .filter((content) => !isWheelchairFilterActive || contentIdsWithWheelchairAccessibleShowing.has(content.id))
            .filter((content) => hasMatchingGenre(content.genres, activeGenreIds))
            .toSorted(contentSortingFunction);
    }, [
        contentSortingFunction,
        showingsCountForContentId,
        props.allContents,
        isNewFilterActive,
        isGretaFilterActive,
        isWheelchairFilterActive,
        contentIdsWithWheelchairAccessibleShowing,
        activeGenreIds,
        timeZone
    ]);

    // Virtual pagination hook
    const { visibleCount, loadMoreRef, hasMore } = useVirtualPagination({
        items: filteredContents,
        config: VIRTUAL_PAGINATION_CONFIG
    });

    // Content-level filters (isNew, isGreta, genre) are applied to the rendered list via
    // `filteredContents`, but NOT by the showing-list query (they are client-only keys). To keep
    // the calendar-bar counts in sync with the list, apply the same filters to the counted showings.
    const filteredContentIds = useMemo(() => {
        const set = new Set<ContentDto['id']>();
        props.allContents.forEach((content) => {
            if (isNewFilterActive && !isMovieReleaseDateNew(content.releaseDate, timeZone)) return;
            if (isGretaFilterActive && !hasMovieAccessibilityFeatures(content.movieAccessibility)) return;
            if (!hasMatchingGenre(content.genres, activeGenreIds)) return;
            set.add(content.id);
        });
        return set;
    }, [props.allContents, isNewFilterActive, isGretaFilterActive, activeGenreIds, timeZone]);

    // Showings that survive the active content-level filters. Wheelchair is showing-level
    // (the showing's cinema room carries the flag), so it is filtered here per showing.
    const countableShowings = useMemo(() => {
        return allShowings.filter((showing) => {
            if (!filteredContentIds.has(showing.contentId)) return false;
            if (isWheelchairFilterActive && !getCinemaRoomById(showing.cinemaRoomId)?.hasWheelchairSpaces) return false;
            return true;
        });
    }, [allShowings, filteredContentIds, isWheelchairFilterActive, getCinemaRoomById]);

    // amount of showings of all contents for each date
    const showingsCounts = useMemo(() => {
        return programDayKeys(timeZone, 14).map((dayKey) => {
            const filterDateStart = programDayStart(dayKey, timeZone).getTime();
            const filterDateEnd = programDayEnd(dayKey, timeZone).getTime();
            return countableShowings.filter((showing) => {
                const showingDate = new Date(showing.startDatetime).getTime();
                return showingDate >= filterDateStart && showingDate < filterDateEnd;
            }).length;
        });
    }, [countableShowings, timeZone]);

    // A date picked from the date picker beyond the visible `visibleDaysInFuture` range gets its
    // own trailing slider box (like cineamo.com). It resolves to `undefined` — and the box
    // disappears — as soon as a date within the range or "all" is selected. Past dates are snapped
    // to today (render-time sync below), so only future out-of-range dates qualify.
    const visibleDateKeys = useMemo(
        () => programDayKeys(timeZone, visibleDaysInFuture),
        [visibleDaysInFuture, timeZone]
    );
    const outOfRangeDayKey = useMemo(() => {
        if (selectedDate === 'all' || selectedDate < dateTodayFormatted || visibleDateKeys.includes(selectedDate)) {
            return undefined;
        }
        const [year, month, day] = selectedDate.split('-').map(Number);
        return year && month && day ? selectedDate : undefined;
    }, [selectedDate, visibleDateKeys, dateTodayFormatted]);

    // The box renders a label, so it gets a process-zone `Date` naming that day; every comparison
    // below stays on the day key.
    const outOfRangeDate = useMemo(
        () => (outOfRangeDayKey ? localDateOfDayKey(outOfRangeDayKey) : undefined),
        [outOfRangeDayKey]
    );

    // Count for the out-of-range box, computed exactly like the in-range items' `showingsCounts`
    // so the badge stays consistent (same source + filters).
    const outOfRangeShowingsCount = useMemo(() => {
        if (!outOfRangeDayKey) {
            return 0;
        }
        const filterDateStart = programDayStart(outOfRangeDayKey, timeZone).getTime();
        const filterDateEnd = programDayEnd(outOfRangeDayKey, timeZone).getTime();
        return countableShowings.filter((showing) => {
            const showingDate = new Date(showing.startDatetime).getTime();
            return showingDate >= filterDateStart && showingDate < filterDateEnd;
        }).length;
    }, [outOfRangeDayKey, countableShowings, timeZone]);

    const hasContentsWithShowings = filteredContents.length > 0;

    const onSelectedDateChange = useCallback(
        (newSelectedDate: SelectedDate) => {
            // Early return if date hasn't changed to prevent unnecessary updates
            if (newSelectedDate === selectedDate) {
                return;
            }

            // Immediately update local state for instant UI response
            setSelectedDate(newSelectedDate);

            // Update URL without navigation (similar to useProgramFilter pattern)
            const newSearchParams = new URLSearchParams(searchParams.toString());

            if (newSelectedDate === dateTodayFormatted) {
                newSearchParams.delete('date');
            } else {
                newSearchParams.set('date', newSelectedDate);
            }

            const newUrlStr = newSearchParams.toString();
            const newUrl = newUrlStr ? `?${newUrlStr}` : '';

            // Only update URL if it's different from current URL
            if (newUrl !== window.location.search) {
                window.history.replaceState(null, '', `${window.location.pathname}${newUrl}`);
            }

            // Track analytics
            const daysBetweenSelectedDateAndCurrentDate =
                newSelectedDate === 'all' ? 'all' : daysBetween(new Date(newSelectedDate), new Date());
            track('Program List Date Selected', {
                date: daysBetweenSelectedDateAndCurrentDate
            });
        },
        [selectedDate, searchParams, setSelectedDate, track, dateTodayFormatted]
    );

    const nearestShowingDate = useMemo(() => {
        // Only compute if we need placeholder (no showings for selected cinemas)
        if (hasContentsWithShowings) return undefined;

        const selectedDayKey = selectedDate === 'all' ? programDayKey(new Date(renderedAt), timeZone) : selectedDate;
        return programDayKeys(timeZone)
            .map((dayKey, index) => ({
                date: dayKey,
                showingsCount: showingsCounts[index],
                daysToSelectedDate: Math.abs(calendarDaysBetween(selectedDayKey, dayKey))
            }))
            .filter(({ showingsCount }) => !!showingsCount)
            .sort((a, b) => a.daysToSelectedDate - b.daysToSelectedDate)[0]?.date;
    }, [showingsCounts, selectedDate, hasContentsWithShowings, renderedAt, timeZone]);

    const [now] = useState(() => Date.now());

    const highlightedShowings = useMemo(() => {
        const source = selectedDate === 'all' ? allShowings : dailyShowings;
        if (!source) return [];
        return source
            .filter((s) => s.highlightAfterDatetime && new Date(s.highlightAfterDatetime).getTime() <= now)
            .map((showing) => {
                const content = props.allContents.find((c) => c.id === showing.contentId);
                return content ? { showing, content } : null;
            })
            .filter((item): item is NonNullable<typeof item> => item !== null);
    }, [selectedDate, now, allShowings, dailyShowings, props.allContents]);

    // Render-time state sync: correct selectedDate if it's a past date (before today)
    if (selectedDate !== null && selectedDate !== 'all' && selectedDate < dateTodayFormatted) {
        setSelectedDate(dateTodayFormatted);
    }

    const programListMobileLayout = domainConfiguration.websiteConfiguration?.programListMobileLayout;
    const programListDesktopLayout = domainConfiguration.websiteConfiguration?.programListDesktopLayout;

    const defaultListElement = (
        <>
            {filteredContents.slice(0, visibleCount).map((content) => (
                <ShowingListItem
                    key={content.id}
                    content={content}
                    selectedDate={selectedDate}
                    showings={showingsByContentId.get(content.id) ?? []}
                    isFetchingShowingList={isFetchingDailyShowingList}
                />
            ))}

            {/* Loading trigger and indicator when more showings are available */}
            {hasMore && (
                <div
                    className="loader-element"
                    ref={loadMoreRef}>
                    <ProgramShowingListSkeleton
                        items={Math.min(VIRTUAL_PAGINATION_CONFIG.loadMore, filteredContents.length - visibleCount)}
                        selectedDate={selectedDate}
                    />
                </div>
            )}
        </>
    );

    // The two breakpoints are configured independently, so each gets its own element. The compact
    // layouts differ between them beyond styling — grouped docks its showtimes panel beside the list
    // on desktop instead of sliding it in over the page — which is why this is not one responsive
    // tree. `null` means "use the default list", which is also what an unset setting resolves to.
    function compactListElementFor(
        layout: WebsiteProgramListLayout | undefined,
        isDesktop: boolean
    ): React.JSX.Element | null {
        if (layout === WebsiteProgramListLayout.COMPACT_SINGLE) {
            return (
                <ProgramCompactList
                    showings={dailyShowings}
                    contents={props.allContents}
                    selectedDate={selectedDate}
                    hasHighlights={highlightedShowings.length > 0}
                    isDesktop={isDesktop}
                />
            );
        }
        if (layout === WebsiteProgramListLayout.COMPACT_GROUPED) {
            return (
                <ProgramCompactGroupedList
                    contents={filteredContents}
                    selectedDate={selectedDate}
                    showingsCountForContentId={showingsCountForContentId}
                    variant={isDesktop ? 'split' : 'overlay'}
                />
            );
        }
        return null;
    }

    const compactMobileListElement = compactListElementFor(programListMobileLayout, false);
    const compactDesktopListElement = compactListElementFor(programListDesktopLayout, true);

    return (
        <div className="flex flex-col">
            <div className="flex flex-col space-y-12 py-16">
                <ProgramFilter />

                <ProgramListCalendar
                    visibleDaysInFuture={visibleDaysInFuture}
                    totalShowingsDaily={showingsCounts}
                    showingsAllCount={countableShowings.length}
                    showLeadingItem="sticky"
                    showTrailingItem="sticky"
                    isLoadingShowingsNumber={isFetchingShowingList}
                    trailingItem={
                        <DatePicker
                            date={selectedDate}
                            onChange={onSelectedDateChange}
                        />
                    }
                    selectedDate={selectedDate}
                    outOfRangeDate={outOfRangeDate}
                    outOfRangeShowingsCount={outOfRangeShowingsCount}
                    onSelectedDateChange={onSelectedDateChange}
                />
            </div>

            <KinofestBanner
                cinemaId={domainConfiguration?.cinema?.id}
                itemSize={KinofestBannerItemSize.thin}
            />

            <ProgramHighlightsSection
                highlights={highlightedShowings}
                selectedDate={selectedDate}
            />

            <div className={cn('flex flex-col', hasContentsWithShowings ? 'order-1 pb-12 lg:pb-24' : 'order-2')}>
                {/*{!isFetchingShowingList && !isAllLocationDetailsAvailable() && (*/}
                {/*    <AddCinemasAllowLocationBanner t={t}/>*/}
                {/*)}*/}

                {/* Show skeleton during initial load */}
                {isFetchingShowingList && (
                    <ProgramShowingListSkeleton
                        items={VIRTUAL_PAGINATION_CONFIG.initialLoad}
                        selectedDate={selectedDate}
                    />
                )}
                {!hasContentsWithShowings && !isFetchingShowingList ? (
                    <ProgramListPlaceholder
                        selectedDate={selectedDate}
                        hasNearestShowingDate={!!nearestShowingDate}
                        onJumpToNearestShowingDate={() =>
                            nearestShowingDate && onSelectedDateChange(nearestShowingDate)
                        }
                    />
                ) : compactMobileListElement || compactDesktopListElement ? (
                    <MobileOrDesktopRendererSSR
                        desktopContent={compactDesktopListElement ?? defaultListElement}
                        mobileContent={compactMobileListElement ?? defaultListElement}
                    />
                ) : (
                    defaultListElement
                )}
            </div>
        </div>
    );
}

export default ProgramList;
